import { PodReviewState, GroupedData, PodItem } from './PodReview.types';
import {
  FETCH_POD_DATA,
  FETCH_POD_DATA_LOADING,
  FETCH_POD_DATA_ERROR,
  FETCH_MASTER_DATA,
  FETCH_MASTER_DATA_LOADING,
  FETCH_MASTER_DATA_ERROR,
  SET_FILTERS,
  SET_GROUP_BY,
  SET_CURRENT_TAB,
  SET_SELECTED_ROWS,
  SET_PAGINATION,
  APPROVE_PODS,
  APPROVE_PODS_LOADING,
  APPROVE_PODS_ERROR,
  REJECT_PODS,
  REJECT_PODS_LOADING,
  REJECT_PODS_ERROR,
  FETCH_VENDOR_OPTIONS,
  FETCH_VENDOR_OPTIONS_LOADING,
  FETCH_VENDOR_OPTIONS_ERROR,
  FETCH_HUB_OPTIONS,
  FETCH_HUB_OPTIONS_LOADING,
  FETCH_HUB_OPTIONS_ERROR,
  DOWNLOAD_PODS,
  DOWNLOAD_PODS_LOADING,
  DOWNLOAD_PODS_ERROR,
  DEFAULT_PAGE_SIZE,
  AI_SUGGESTION_ORDER
} from './PodReview.constants';

const defaultState: PodReviewState = {
  // Data
  podData: [],
  groupedData: [],
  masterData: {
    column_list: {
      pending: [],
      approved: [],
      rejected: []
    },
    permissions: {
      view_pod: false,
      approve_pod: false
    }
  },

  // UI State
  isLoading: false,
  isMasterDataLoading: false,
  errorMessage: null,
  selectedRowKeys: [],
  currentTab: 'pending',
  groupBy: 'AI-SUGGEST',

  // Filters & Pagination
  appliedFilters: {},
  pagination: {
    currentPage: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    isNextPresent: false
  },

  // Dropdown Options
  vendorOptions: [],
  hubOptions: [],
  isLoadingVendors: false,
  isLoadingHubs: false
};

// Helper function to create grouped data
const createGroupedData = (data: PodItem[], groupBy: 'AI-SUGGEST' | 'VENDOR'): GroupedData[] => {
  if (!data || data.length === 0) return [];

  const groups: { [key: string]: PodItem[] } = {};

  data.forEach(item => {
    let groupKey: string;
    if (groupBy === 'AI-SUGGEST') {
      // Check if ai_recommendation is missing, null, undefined, or empty
      const aiRec = item.ai_recommendation?.trim();
      groupKey = aiRec ? aiRec.toUpperCase() : 'NOT_AVAILABLE';
    } else {
      groupKey = item.vendor_name;
    }
    
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(item);
  });

  // Convert groups to array
  const groupedArray = Object.keys(groups).map(groupKey => ({
    groupKey,
    groupTitle: groupBy === 'AI-SUGGEST'
      ? getAISuggestionTitle(groupKey)
      : groupKey,
    items: groups[groupKey],
    isExpanded: true // Default to expanded
  }));

  // Sort groups by AI suggestion order if grouping by AI-SUGGEST
  if (groupBy === 'AI-SUGGEST') {
    groupedArray.sort((a, b) => {
      const orderA = AI_SUGGESTION_ORDER[a.groupKey as keyof typeof AI_SUGGESTION_ORDER] || 999;
      const orderB = AI_SUGGESTION_ORDER[b.groupKey as keyof typeof AI_SUGGESTION_ORDER] || 999;
      return orderA - orderB;
    });
  }

  return groupedArray;
};

// Helper function to get AI suggestion title
const getAISuggestionTitle = (suggestion: string): string => {
  switch (suggestion) {
    case 'APPROVE':
      return 'Approve';
    case 'REVIEW':
      return 'Review';
    case 'REJECT':
      return 'Reject';
    case 'NOT_AVAILABLE':
      return 'AI analysis not available';
    default:
      return suggestion;
  }
};

export default function podReviewReducer(state = defaultState, action: any): PodReviewState {
  switch (action.type) {
    // Master Data Actions
    case FETCH_MASTER_DATA:
      return {
        ...state,
        masterData: action.data,
        errorMessage: null
      };

    case FETCH_MASTER_DATA_LOADING:
      return {
        ...state,
        isMasterDataLoading: action.data
      };

    case FETCH_MASTER_DATA_ERROR:
      return {
        ...state,
        errorMessage: action.error
      };

    // POD Data Actions
    case FETCH_POD_DATA:
      const groupedData = createGroupedData(action.data.data, state.groupBy);
      return {
        ...state,
        podData: action.data.data,
        groupedData,
        pagination: {
          ...state.pagination,
          total: action.data.total,
          currentPage: action.data.page,
          isNextPresent: action.data.isNextPresent
        },
        errorMessage: null
      };

    case FETCH_POD_DATA_LOADING:
      return {
        ...state,
        isLoading: action.data
      };

    case FETCH_POD_DATA_ERROR:
      return {
        ...state,
        errorMessage: action.error
      };

    // Filter Actions
    case SET_FILTERS:
      return {
        ...state,
        appliedFilters: action.filters
      };

    case SET_GROUP_BY:
      // Clear previous data and set loading to true to prevent race conditions
      return {
        ...state,
        groupBy: action.groupBy,
        podData: [],
        groupedData: [],
        isLoading: true
      };

    case SET_CURRENT_TAB:
      // Clear previous data and set loading to true to prevent race conditions
      return {
        ...state,
        currentTab: action.tab,
        podData: [],
        groupedData: [],
        isLoading: true,
        errorMessage: null
      };

    case SET_SELECTED_ROWS:
      return {
        ...state,
        selectedRowKeys: action.selectedRowKeys
      };

    case SET_PAGINATION:
      // If page is changing, set loading to true to prevent showing stale data
      const isPageChanging = action.pagination.currentPage &&
        action.pagination.currentPage !== state.pagination.currentPage;
      return {
        ...state,
        pagination: {
          ...state.pagination,
          ...action.pagination
        },
        ...(isPageChanging && { isLoading: true })
      };

    // Approve Actions
    case APPROVE_PODS:
      return {
        ...state,
        errorMessage: null
      };

    case APPROVE_PODS_LOADING:
      return {
        ...state,
        isLoading: action.data
      };

    case APPROVE_PODS_ERROR:
      return {
        ...state,
        errorMessage: action.error
      };

    // Reject Actions
    case REJECT_PODS:
      return {
        ...state,
        errorMessage: null
      };

    case REJECT_PODS_LOADING:
      return {
        ...state,
        isLoading: action.data
      };

    case REJECT_PODS_ERROR:
      return {
        ...state,
        errorMessage: action.error
      };

    // Download Actions
    case DOWNLOAD_PODS:
      return {
        ...state,
        errorMessage: null
      };

    case DOWNLOAD_PODS_LOADING:
      return {
        ...state,
        isLoading: action.data
      };

    case DOWNLOAD_PODS_ERROR:
      return {
        ...state,
        errorMessage: action.error
      };

    // Vendor Options Actions
    case FETCH_VENDOR_OPTIONS:
      return {
        ...state,
        vendorOptions: action.data
      };

    case FETCH_VENDOR_OPTIONS_LOADING:
      return {
        ...state,
        isLoadingVendors: action.data
      };

    case FETCH_VENDOR_OPTIONS_ERROR:
      return {
        ...state,
        vendorOptions: []
      };

    // Hub Options Actions
    case FETCH_HUB_OPTIONS:
      return {
        ...state,
        hubOptions: action.data
      };

    case FETCH_HUB_OPTIONS_LOADING:
      return {
        ...state,
        isLoadingHubs: action.data
      };

    case FETCH_HUB_OPTIONS_ERROR:
      return {
        ...state,
        hubOptions: []
      };

    default:
      return state;
  }
}